import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Autocomplete,
  Chip,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import PartySelector from '@/components/selectors/PartySelector';
import dayjs from 'dayjs';
import api from '@/services/ApiService';
import { v4 as uuidv4 } from 'uuid';
import proxy from '@/services/ProxyService';
import { useRouteLoaderData } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import ContractTimeSelector from './content/ContractTimeSelector';
import { fetchWeclappPrice } from './content/fetchWeclappPrice';
import DraggablePaper from '@/components/DraggablePaper';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DEFAULT_PRODUCTS = [
  {
    name: 'Microsoft 365',
    image:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Microsoft_365_%282022%29.svg/1862px-Microsoft_365_%282022%29.svg.png',
    initials: 'M365',
  },
  {
    name: 'Acronis',
    image:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/Acronis.svg/2560px-Acronis.svg.png',
    initials: 'ACR',
  },
  {
    name: 'Starface',
    image:
      'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Microsoft_365_%282022%2adsf9.svg/1862px-Microsoft_365_%282022%29.svg.png',
    initials: 'SF',
  },
];

const getInitialState = (data, defaultGroupNames) => ({
  partyId: data?.partyId || null,
  partyName: data?.partyName || '',
  title: data?.title || 'IT-Service Vertrag',
  description: data?.description || '',
  contractNumber: data?.contractNumber || '',
  contractDate: data?.contractDate ? dayjs(data.contractDate) : dayjs(),
  billingFrequency: data?.billingFrequency || 'monatlich',
  products: data?.products || DEFAULT_PRODUCTS,
  contractItems: data?.contractItems || [],
  contractStatus: data?.contractStatus || 'ACTIVE',
  groupNames: data?.groupNames || [
    ...(defaultGroupNames || []).map((name) => ({
      uuid: uuidv4(),
      title: name,
    })),
  ],
});

export default function ContractEditDialog({ data, open, onClose, onSave }) {
  const theme = useTheme();
  const {
    fixedDeploymentConfigs: { contracts },
    me,
  } = useRouteLoaderData('auth');
  const weclappInterface = contracts?.interfaces?.weclapp;
  const [formState, setFormState] = useState(
    getInitialState(data, contracts?.defaultGroupNames || []),
  );
  const [selectedParty, setSelectedParty] = useState(null);

  const [inputValue, setInputValue] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const inputRef = useRef(null);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('idle');
  const [verificationMessage, setVerificationMessage] = useState('');

  const invoiceInputRef = useRef(null);
  const invoiceRequestIdRef = useRef(0);
  const invoiceAbortControllerRef = useRef(null);

  const isEditMode = !!data?._id || !!data?.partyId;

  useEffect(() => {
    let isActive = true;
    const loadData = async () => {
      if (open) {
        setFormState(getInitialState(data, contracts?.defaultGroupNames || []));
        setInputValue('');
        setEditingGroup(null);

        setInvoiceNumber('');
        setVerificationStatus('idle');
        setVerificationMessage('');

        if (data?.partyId) {
          try {
            const { data: partyData } = await api.get(`/party/${data.partyId}`);
            if (isActive) {
              setSelectedParty(partyData);
              setFormState((prevState) => ({
                ...prevState,
                partyId: partyData._id,
                partyName:
                  partyData.company ||
                  `${partyData.firstName} ${partyData.lastName}`.trim(),
              }));
            }
          } catch (e) {
            console.error(e);
          }
        } else {
          if (isActive) setSelectedParty(null);
        }
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [open, data, contracts?.defaultGroupNames]);

  useEffect(() => {
    if (selectedParty) {
      if (!isEditMode) {
        setFormState((prevState) => ({
          ...prevState,
          partyId: selectedParty._id,
          partyName:
            selectedParty.company ||
            `${selectedParty.firstName} ${selectedParty.lastName}`.trim(),
        }));
      }
    }
  }, [selectedParty, isEditMode]);

  const handleFieldChange = (field) => (event) => {
    setFormState({ ...formState, [field]: event.target.value });
  };

  const handleInvoiceChange = (e) => {
    const raw = e.target.value || '';
    const trimmed = raw.replace(/^\s+|\s+$/g, '');
    setInvoiceNumber(trimmed);
    if (verificationStatus !== 'idle') {
      setVerificationStatus('idle');
      setVerificationMessage('');
    }
  };

  const handleDateChange = (newDate) => {
    setFormState({ ...formState, contractDate: newDate });
  };

  const handleBillingChange = (event, newFrequency) => {
    if (newFrequency !== null) {
      setFormState({ ...formState, billingFrequency: newFrequency });
    }
  };

  const handleStartEdit = (groupToEdit) => {
    setEditingGroup(groupToEdit);
    setInputValue(groupToEdit.title);
    const remainingGroups = formState.groupNames.filter(
      (g) => g.uuid !== groupToEdit.uuid,
    );
    setFormState({ ...formState, groupNames: remainingGroups });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleGroupNamesChange = (event, newValue) => {
    const processedValues = newValue.map((item) => {
      if (typeof item === 'string') {
        if (editingGroup) {
          return { uuid: editingGroup.uuid, title: item };
        } else {
          return { uuid: uuidv4(), title: item };
        }
      }
      return item;
    });
    setFormState({ ...formState, groupNames: processedValues });
    setEditingGroup(null);
    setInputValue('');
  };

  const handleInputBlur = () => {
    if (editingGroup && inputValue.trim() !== '') {
      const newValue = [
        ...formState.groupNames,
        { uuid: editingGroup.uuid, title: inputValue },
      ];
      handleGroupNamesChange(null, newValue);
    } else if (editingGroup && inputValue.trim() === '') {
      setEditingGroup(null);
      setInputValue('');
    }
  };

  const handleCheckInvoice = async () => {
    if (!invoiceNumber.trim()) return;
    const requestId = ++invoiceRequestIdRef.current;
    try {
      invoiceAbortControllerRef.current?.abort();
    } catch (e) {}
    invoiceAbortControllerRef.current = new AbortController();
    setVerificationStatus('loading');
    setVerificationMessage('');
    try {
      invoiceInputRef.current?.focus();
      const el = invoiceInputRef.current;
      if (el && el.setSelectionRange) {
        const len = el.value?.length || 0;
        el.setSelectionRange(len, len);
      }
    } catch (e) {}
    try {
      const {
        data: { result: invoices },
      } = await proxy.get(
        `${weclappInterface}/salesInvoice?invoiceNumber-ilike=${invoiceNumber}`,
        { signal: invoiceAbortControllerRef.current.signal },
      );
      if (requestId !== invoiceRequestIdRef.current) return;
      const invoice = invoices?.[0];
      if (invoice) {
        const { salesInvoiceItems: salesItems = [], customerId } = invoice;
        const childrenMap = {};
        salesItems.forEach((item) => {
          if (item.parentItemId) {
            if (!childrenMap[item.parentItemId]) {
              childrenMap[item.parentItemId] = [];
            }
            childrenMap[item.parentItemId].push(item);
          }
        });
        const invoiceGroupNames = [
          ...new Set(salesItems.map((item) => item.groupName).filter(Boolean)),
        ].map((name) => ({
          uuid: uuidv4(),
          title: name,
        }));

        const parentItems = salesItems.filter((item) => !item.parentItemId);
        const newContractItems = [];

        for (const parent of parentItems) {
          await delay(50);
          const {
            data: { result: contractData },
          } = await proxy.get(
            `${weclappInterface}/contract?contractItems.id-eq=${parent.contractItemId}`,
            { signal: invoiceAbortControllerRef.current.signal },
          );
          const pricingDateParent = contractData[0]?.pricingDate
            ? new Date(contractData[0]?.pricingDate)
            : null;

          const children = childrenMap[parent.id] || [];
          const isBOM = children.length > 0;
          let pricingDate = pricingDateParent
            ? pricingDateParent
            : parent?.servicePeriodFrom
              ? new Date(parent?.servicePeriodFrom)
              : new Date(parent?.servicePeriodTo);

          if (parent.manualUnitPrice === true) {
            const contract = contractData?.[0];
            if (contract) {
              const contractItem = contract.contractItems.find(
                (item) => item.id === parent.contractItemId,
              );
              pricingDate = pricingDateParent
                ? pricingDateParent
                : contractItem?.servicePeriodToDate
                  ? new Date(contractItem.servicePeriodToDate)
                  : new Date(contractItem.servicePeriodFromDate);
            }
          }

          const { price: parentPrice, discountPercentage } =
            await fetchWeclappPrice({
              articleId: parent.articleId,
              customerId: customerId,
              pricingDate: pricingDate,
              quantity:
                parent.type === 'STATIC'
                  ? parent.quantity || 0
                  : parent.quantity?.min || 0,
              weclappApiUrl: weclappInterface,
            });
          let parentArticle = null;
          try {
            const { data } = await proxy.get(
              `${weclappInterface}/article/id/${parent.articleId}`,
              { signal: invoiceAbortControllerRef.current.signal },
            );
            parentArticle = data;
          } catch (e) {
            if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
              throw e;
            }
            console.warn('Konnte Artikel nicht laden:', parent.articleId, e);
          }

          const salesBillOfMaterialItems = await Promise.all(
            children.map(async (child) => {
              const childMatch = parentArticle?.salesBillOfMaterialItems?.find(
                (item) => item.articleId === child.articleId,
              );

              const { discountPercentage: childDiscount } =
                await fetchWeclappPrice({
                  articleId: childMatch?.articleId || child.articleId,
                  customerId: customerId,
                  pricingDate: pricingDate,
                  quantity:
                    parent.type === 'STATIC'
                      ? parent.quantity || 0
                      : parent.quantity?.min || 0,
                  weclappApiUrl: weclappInterface,
                });

              const finalChildDiscount = child.discountPercentage
                ? -Math.abs(Number(child.discountPercentage))
                : -Math.abs(Number(childDiscount));

              return {
                uuid: uuidv4(),
                type: 'STATIC',
                articleId: child.articleId,
                articleNumber: child.articleNumber,
                quantity: Number(child.quantity),
                title: child.title,
                pricingDate: pricingDate,
                description: child.description,
                unitPrice: Number(child.unitPrice),
                unchangedUnitPrice: Number(child.unitPrice),
                discountPercentage: Number(finalChildDiscount),
                factor: childMatch?.quantity ? Number(childMatch?.quantity) : 1,
                articleFactor: childMatch?.quantity
                  ? Number(childMatch?.quantity)
                  : 1,
              };
            }),
          );

          let calculatedDiscount = discountPercentage;
          if (
            parent?.discountPercentage &&
            Number(parent.discountPercentage) !== 0
          ) {
            calculatedDiscount = -Math.abs(Number(parent.discountPercentage));
          }

          newContractItems.push({
            uuid: uuidv4(),
            type: 'STATIC',
            discountPercentage: calculatedDiscount,
            articleType:
              parentArticle?.articleType ||
              (isBOM ? 'SALES_BILL_OF_MATERIAL' : 'BASIC'),
            articleId: parent.articleId,
            articleNumber: parent.articleNumber,
            quantity: Number(parent.quantity),
            title: parent.title,
            description: parent.description,
            unitPrice: Number(parentPrice),
            unchangedUnitPrice: Number(parentPrice),
            pricingDate: pricingDate,
            salesBillOfMaterialItems: salesBillOfMaterialItems,
            groupName:
              invoiceGroupNames.find(
                (group) => group.title === parent?.groupName,
              )?.uuid || '',
            useSalesBillOfMaterialItemPrices:
              parentArticle?.useSalesBillOfMaterialItemPrices,
          });
        }

        const subPositionCount = salesItems.filter(
          (item) => item.parentItemId,
        ).length;
        const mainPositionCount = parentItems.length;

        setFormState((prev) => ({
          ...prev,
          groupNames:
            invoiceGroupNames?.length > 0 ? invoiceGroupNames : prev.groupNames,
          title: invoice.title || prev.title,
          description: invoice.commission || prev.description,
          contractItems: newContractItems,
        }));

        if (requestId === invoiceRequestIdRef.current) {
          setVerificationStatus('success');
          setVerificationMessage(
            `Gefunden: ${mainPositionCount} Hauptpositionen und ${subPositionCount} Unterpositionen. Preise wurden aktualisiert.`,
          );
        }
      } else {
        if (requestId === invoiceRequestIdRef.current) {
          setVerificationStatus('error');
          setVerificationMessage('Keine Rechnung mit dieser Nummer gefunden.');
        }
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return;
      }
      if (requestId === invoiceRequestIdRef.current) {
        setVerificationStatus('error');
        setVerificationMessage('Fehler bei der Abfrage der Rechnungsnummer.');
      }
    }
  };

  const handleSaveClick = () => {
    let currentGroupNames = [...formState.groupNames];
    if (inputValue.trim() !== '') {
      const newItem = editingGroup
        ? { uuid: editingGroup.uuid, title: inputValue }
        : { uuid: uuidv4(), title: inputValue };
      currentGroupNames.push(newItem);
    }
    const contractDataToSend = {
      ...formState,
      groupNames: currentGroupNames,
      contractDate: formState.contractDate.toISOString(),
      ...(data?._id && { _id: data._id }),
      ...(data?.version !== undefined && { version: data.version }),
      sharedWith: [...(contracts?.contractShares || [])],
    };
    onSave(contractDataToSend);
  };

  const isSaveDisabled =
    !formState.partyId ||
    (invoiceNumber.trim() !== '' && verificationStatus !== 'success');

  useEffect(() => {
    if (!invoiceNumber.trim()) return;
    const handler = setTimeout(() => {
      handleCheckInvoice();
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  useEffect(() => {
    return () => {
      try {
        invoiceAbortControllerRef.current?.abort();
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    if (!open) {
      try {
        invoiceAbortControllerRef.current?.abort();
      } catch (e) {}
      invoiceRequestIdRef.current++;
      setVerificationStatus('idle');
      setVerificationMessage('');
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle
        id="draggable-dialog-title"
        className="draggable-handle"
        sx={{
          cursor: 'move',
          userSelect: 'none',
          padding: '16px 24px',
          backgroundColor: 'background.paper',
        }}
      >
        {isEditMode ? 'Vertragsdaten bearbeiten' : 'Neuen Vertrag erstellen'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: '24px !important',
        }}
      >
        <PartySelector
          party={selectedParty}
          setParty={setSelectedParty}
          customer={true}
          disabled={isEditMode}
        />

        <TextField
          label="Bezeichnung"
          value={formState.title}
          onChange={handleFieldChange('title')}
          fullWidth
          variant="outlined"
        />

        <TextField
          label="Beschreibung"
          value={formState.description}
          onChange={handleFieldChange('description')}
          fullWidth
          variant="outlined"
          multiline
          rows={3}
        />

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={formState.groupNames}
          onChange={handleGroupNamesChange}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.title
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              const label = typeof option === 'string' ? option : option.title;
              return (
                <Chip
                  key={key}
                  variant="outlined"
                  label={label}
                  {...tagProps}
                  onClick={() => {
                    if (typeof option !== 'string') {
                      handleStartEdit(option);
                    }
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      cursor: 'pointer',
                      borderColor: 'primary.main',
                    },
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              variant="outlined"
              label="Gruppen"
              placeholder="Gruppe eingeben"
              helperText="Klicken Sie auf eine Gruppe, um sie zu bearbeiten."
              onBlur={handleInputBlur}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            label="Vertragsnummer"
            value={formState.contractNumber}
            onChange={handleFieldChange('contractNumber')}
            variant="outlined"
            sx={{ flex: 1 }}
          />
          <ContractTimeSelector
            label="Vertragsdatum"
            date={formState.contractDate}
            onDateChange={handleDateChange}
            theme={theme}
            variant="outlined"
            size="small"
            flex={1}
            height={40}
          />
        </Box>

        {!isEditMode && (
          <TextField
            size="small"
            label="Rechnungsnummer (Optional)"
            value={invoiceNumber}
            onChange={handleInvoiceChange}
            fullWidth
            variant="outlined"
            placeholder="Rechnungsnummer eingeben"
            helperText={
              verificationStatus === 'loading'
                ? 'Rechnung wird geprüft...'
                : verificationMessage ||
                  'Geben Sie eine Rechnungsnummer ein, um Positionen zu importieren.'
            }
            error={verificationStatus === 'error'}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                invoiceNumber.trim() &&
                verificationStatus !== 'loading'
              ) {
                e.preventDefault();
                handleCheckInvoice();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCheckInvoice}
                    edge="end"
                    disabled={
                      !invoiceNumber.trim() || verificationStatus === 'loading'
                    }
                    color={
                      verificationStatus === 'success' ? 'success' : 'primary'
                    }
                  >
                    {verificationStatus === 'loading' ? (
                      <CircularProgress size={24} />
                    ) : verificationStatus === 'success' ? (
                      <CheckCircleIcon />
                    ) : verificationStatus === 'error' ? (
                      <ErrorIcon color="error" />
                    ) : (
                      <SearchIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiFormHelperText-root': {
                color:
                  verificationStatus === 'success' ? 'success.main' : undefined,
              },
            }}
            inputRef={invoiceInputRef}
          />
        )}

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Abrechnung
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={formState.billingFrequency}
            exclusive
            onChange={handleBillingChange}
            fullWidth
          >
            <ToggleButton value="monatlich">Monatlich</ToggleButton>
            <ToggleButton value="jährlich">Jährlich</ToggleButton>
            <ToggleButton value="no_billing">Keine Abrechnung</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleSaveClick}
          variant="contained"
          disabled={isSaveDisabled || verificationStatus === 'loading'}
        >
          {isEditMode ? 'Speichern' : 'Vertrag erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
